/**
 * PMC Gradebook (Programme > Gradebook) API calls with React Query.
 * Use QUERY_KEYS.pmcGradebook.root to invalidate everything under this domain.
 */
import { getPMCFiltersCancelToken } from '../../services/GradeSettingService';
import { getProgramGradebookModuleList, getProgrammeModulesGradeStudentList } from '../../services/ProgrammeServices';
import { QUERY_CACHE_PROFILES, QUERY_KEYS } from '../queryConsts';
import { useBaseReactQuery } from './useBaseReactQuery';

const EMPTY_MODULE_HEADERS = Object.freeze([]);
const EMPTY_DROPDOWN_FILTERS = Object.freeze({ intake: [], graduated: [], students: [], totalStudents: 0 });
const EMPTY_STUDENT_GRADE_LIST = Object.freeze({
    data: [],
    qualifications: undefined,
    pagination: {
        from: 0,
        to: 0,
        total: 0,
        perPage: 0,
        currentPage: 0,
        lastPage: 0,
    },
});

/**
 * Builds the cache key for {@link useStudentGradeList}, so callers can target the
 * same cached entry (e.g. an optimistic exemption-toggle update via `queryClient.setQueryData`).
 * @param {object} requestData Table payload passed to getProgrammeModulesGradeStudentList.
 */
export function studentGradeListQueryKey(requestData) {
    return [QUERY_KEYS.pmcGradebook.root, QUERY_KEYS.pmcGradebook.studentGradeList, requestData];
}

/**
 * Module column headers for the gradebook table - NOT PERSISTED
 * @param {object} params
 * @param {string | number} params.programmeId Programme id.
 * @param {string | number} params.versionId Version id.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useModuleColumnHeaders({ programmeId, versionId }) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.pmcGradebook.root, QUERY_KEYS.pmcGradebook.moduleColumnHeaders, programmeId, versionId],
        async () => {
            const { data } = await getProgramGradebookModuleList({ id: programmeId, versionId });
            return data?.getModuls ?? EMPTY_MODULE_HEADERS;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            enabled: Boolean(programmeId),
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_MODULE_HEADERS,
    };
}

/**
 * Intake/Graduated dropdown filter options for the gradebook - PERSISTED
 * @param {object} params
 * @param {string | number} params.programmeId Programme id.
 * @param {string | number} params.versionId Version id.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useIntakeAndGraduationDropdownFilters({ programmeId, versionId }) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.pmcGradebook.root, QUERY_KEYS.pmcGradebook.intakeAndGraduationDropdownFilters, programmeId, versionId],
        async () => {
            const { data } = await getPMCFiltersCancelToken({ id: programmeId, versionId });
            return {
                intake: data?.intakeFilters ?? [],
                graduated: data?.graduatedFilter ?? [],
                students: data?.students ?? [],
                totalStudents: data?.totalStudents ?? 0,
            };
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.LOOKUP,
            enabled: Boolean(programmeId),
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_DROPDOWN_FILTERS,
    };
}

/**
 * Paginated/searched/filtered/sorted student grade rows for the gradebook table - NOT PERSISTED
 * @param {object} requestData Table payload (id, versionId, key, sort, search, page, limit, intake_year_number, graduation, students).
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useStudentGradeList(requestData) {
    const query = useBaseReactQuery(
        studentGradeListQueryKey(requestData),
        async () => {
            const { data: gradeListData } = await getProgrammeModulesGradeStudentList(requestData);
            return {
                data: gradeListData?.data ?? [],
                qualifications: gradeListData?.getQulification,
                pagination: {
                    from: gradeListData?.pagination?.from,
                    to: gradeListData?.pagination?.to,
                    total: gradeListData?.pagination?.total,
                    perPage: gradeListData?.pagination?.per_page,
                    currentPage: gradeListData?.pagination?.current_page,
                    lastPage: gradeListData?.pagination?.last_page,
                },
            };
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.TABLE,
            enabled: Boolean(requestData?.id),
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_STUDENT_GRADE_LIST,
    };
}
